import { PageHeader } from "@/components/ui/design-system";
import { Select as DesignSelect } from "@/components/ui/design-system";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { Badge, type Ton } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import {
  dohvatiStatusePodobnosti,
  dohvatiRokove,
  type PojedinostiPodobnosti,
  type RazlogPodobnosti,
  type Rok,
  type SkupnaPodobnost,
  type StatusPodobnosti,
} from "@/lib/eligibility";
import { datum } from "@/lib/format";
import { porukaDohvata } from "@/lib/greske";
import { nazivStanja } from "@/lib/stanja";
import { cn } from "@/lib/utils";
import { CompanyDeadlines } from "@/components/obveze/CompanyDeadlines";
import { ObligationTasks } from "@/components/obveze/ObligationTasks";

const RAZDOBLJA = [30, 60, 90, 120] as const;

type AktivniTab = "podobnost" | "rokovi" | "company" | "tasks";
type VremenskaGrupa = "isteklo" | "do7" | "do15" | "do30" | "vise30";
type TipObveze =
  | "ra2"
  | "znr"
  | "ppe"
  | "dozvola"
  | "kvalifikacija"
  | "vozacka"
  | "dokument";

interface RedakPodobnosti {
  radnik: SkupnaPodobnost;
  pojedinosti: PojedinostiPodobnosti;
}

interface Podaci {
  podobnosti: RedakPodobnosti[];
  rokovi: Rok[];
}

const VREMENSKE_GRUPE: { kljuc: VremenskaGrupa; naziv: string }[] = [
  { kljuc: "isteklo", naziv: "ISTEKLO" },
  { kljuc: "do7", naziv: "≤7 dana" },
  { kljuc: "do15", naziv: "≤15" },
  { kljuc: "do30", naziv: "≤30" },
  { kljuc: "vise30", naziv: "30+" },
];

const TIPOVI_OBVEZA: { kljuc: TipObveze; naziv: string }[] = [
  { kljuc: "ra2", naziv: "RA-2" },
  { kljuc: "znr", naziv: "ZNR" },
  { kljuc: "ppe", naziv: "OZO/PPE" },
  { kljuc: "dozvola", naziv: "Dozvola" },
  { kljuc: "kvalifikacija", naziv: "Kvalifikacija" },
  { kljuc: "vozacka", naziv: "Vozačka" },
  { kljuc: "dokument", naziv: "Opći dokument" },
];

function oznakaPodobnosti(status: StatusPodobnosti): { tekst: string; ton: Ton } {
  return status === "podoban"
    ? { tekst: "Podoban", ton: "ok" }
    : { tekst: "Nepodoban", ton: "bad" };
}

function danaDoIsteka(dana: number): string {
  if (dana === 0) return "istječe danas";
  if (dana === 1) return "1 dan";
  if (dana === -1) return "isteklo prije 1 dan";
  if (dana < 0) return `isteklo prije ${Math.abs(dana)} dana`;
  return `${dana} dana`;
}

function tipZaStavku(
  izvor: string,
  vrsta = "",
  obveza = "",
): TipObveze | null {
  const source = izvor.toLocaleLowerCase("hr");
  const tekst = `${source} ${vrsta} ${obveza}`.toLocaleLowerCase("hr");

  if (source === "ra2") return "ra2";
  if (source === "znr") return "znr";
  if (source === "ppe") return "ppe";
  if (source === "dozvola") return "dozvola";
  if (source === "kvalifikacija") return "kvalifikacija";
  if (source === "dokument") {
    return tekst.includes("vozač") ||
      tekst.includes("vozack") ||
      tekst.includes("driving_licence")
      ? "vozacka"
      : "dokument";
  }

  if (
    tekst.includes("vozač") ||
    tekst.includes("vozack") ||
    tekst.includes("driving_licence")
  ) return "vozacka";
  if (tekst.includes("ppe") || tekst.includes("ozo")) return "ppe";
  if (tekst.includes("ra-2") || tekst.includes("ra2") || tekst.includes("liječ")) return "ra2";
  if (tekst.includes("znr") || tekst.includes("osposob")) return "znr";
  if (tekst.includes("kvalifik") || tekst.includes("licenc")) return "kvalifikacija";
  if (tekst.includes("dozvol")) return "dozvola";
  if (tekst.includes("dokument")) return "dokument";
  return null;
}

function odredisteRadnika(
  radnikId: string,
  izvor: string,
  vrsta = "",
  obveza = "",
): string {
  const osnova = `/zaposlenici/${radnikId}`;
  switch (tipZaStavku(izvor, vrsta, obveza)) {
    case "ra2":
      return `${osnova}?tab=zdravlje`;
    case "znr":
      return `${osnova}?tab=znr#znr-osposobljavanje`;
    case "ppe":
      return `${osnova}?tab=znr#ozo`;
    case "dozvola":
      return `${osnova}?tab=dozvole`;
    case "kvalifikacija":
      return `${osnova}?tab=kvalifikacije`;
    case "vozacka":
      return `${osnova}?tab=identitet#vozacka-dozvola`;
    case "dokument":
      return `${osnova}?tab=dokumenti`;
    default:
      return `${osnova}?tab=osnovno`;
  }
}

function grupaZaRok(dana: number): VremenskaGrupa {
  if (dana < 0) return "isteklo";
  if (dana <= 7) return "do7";
  if (dana <= 15) return "do15";
  if (dana <= 30) return "do30";
  return "vise30";
}

export function RokoviObvezePage() {
  const { can } = useAuth();
  const smijePregledati = can("radnik", "read");
  const smijeVidjetiObvezeTvrtke = can("company_obligation", "read");
  const smijeVidjetiZadatke = can("obligation_task", "read");
  const [days, setDays] = useState<number>(120);
  const [aktivniTab, setAktivniTab] = useState<AktivniTab>(smijePregledati ? "podobnost" : smijeVidjetiObvezeTvrtke ? "company" : "tasks");
  const [vremenskiFiltar, setVremenskiFiltar] = useState<VremenskaGrupa | null>(null);
  const [tipovi, setTipovi] = useState<Set<TipObveze>>(() => new Set());
  const [pokusaj, setPokusaj] = useState(0);
  const [odgovor, setOdgovor] = useState<{ kljuc: string; podaci: Podaci } | null>(null);
  const [greska, setGreska] = useState<{ kljuc: string; poruka: string } | null>(null);
  const [otvoreni, setOtvoreni] = useState<string | null>(null);
  const kljuc = `${days}|${pokusaj}`;

  useEffect(() => {
    if (!smijePregledati || aktivniTab === "company" || aktivniTab === "tasks") return;

    const kontroler = new AbortController();

    const ucitaj = async () => {
      try {
        const [statusi, rokovi] = await Promise.all([
          dohvatiStatusePodobnosti(kontroler.signal),
          dohvatiRokove(days, kontroler.signal),
        ]);

        if (kontroler.signal.aborted) return;
        setOdgovor({
          kljuc,
          podaci: {
            podobnosti: statusi.map((radnik) => ({
              radnik,
              pojedinosti: {
                worker_id: radnik.worker_id,
                worker_name: radnik.worker_name,
                eligibility: radnik.eligibility,
              },
            })),
            rokovi: rokovi
              .filter(
                (rok) =>
                  rok.source !== "company_obligation" &&
                  rok.source !== "site_obligation",
              )
              .sort((a, b) => a.due_on.localeCompare(b.due_on)),
          },
        });
      } catch (error: unknown) {
        if (kontroler.signal.aborted) return;
        setGreska({ kljuc, poruka: porukaDohvata(error) });
      }
    };

    void ucitaj();
    return () => kontroler.abort();
  }, [days, kljuc, pokusaj, smijePregledati, aktivniTab]);

  const podaci = odgovor?.kljuc === kljuc ? odgovor.podaci : null;
  const rokoviPoTipu = useMemo(
    () =>
      (podaci?.rokovi ?? []).filter((rok) => {
        if (tipovi.size === 0) return true;
        const tip = tipZaStavku(rok.source, rok.item_type, rok.obligation);
        return tip !== null && tipovi.has(tip);
      }),
    [podaci?.rokovi, tipovi],
  );
  const brojPoGrupi = useMemo(
    () =>
      Object.fromEntries(
        VREMENSKE_GRUPE.map(({ kljuc: grupa }) => [
          grupa,
          rokoviPoTipu.filter((rok) => grupaZaRok(rok.days_until_due) === grupa).length,
        ]),
      ) as Record<VremenskaGrupa, number>,
    [rokoviPoTipu],
  );
  const grupiraniRokovi = useMemo(
    () =>
      VREMENSKE_GRUPE.map((grupa) => ({
        ...grupa,
        rokovi: rokoviPoTipu.filter(
          (rok) =>
            grupaZaRok(rok.days_until_due) === grupa.kljuc &&
            (vremenskiFiltar === null || vremenskiFiltar === grupa.kljuc),
        ),
      })).filter((grupa) => grupa.rokovi.length > 0),
    [rokoviPoTipu, vremenskiFiltar],
  );

  if (!smijePregledati && !smijeVidjetiObvezeTvrtke && !smijeVidjetiZadatke) return <Navigate to="/" replace />;

  if ((aktivniTab === "podobnost" || aktivniTab === "rokovi") && greska?.kljuc === kljuc) {
    return (
      <div className="avm-surface overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
        <EmptyState
          naslov="Rokovi i obveze se ne mogu učitati"
          objasnjenje={greska.poruka}
          akcija={<Button onClick={() => setPokusaj((vrijednost) => vrijednost + 1)}>Pokušaj ponovno</Button>}
        />
      </div>
    );
  }

  if ((aktivniTab === "podobnost" || aktivniTab === "rokovi") && !podaci) {
    return (
      <div className="flex flex-col gap-6">
        <h1 className="text-[var(--text-title)] leading-tight font-bold">Rokovi i obveze</h1>
        <SkeletonTablica redaka={5} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Rokovi i obveze" meta="Podobnost za rad i nadolazeći rokovi aktivnih zaposlenika." actions={<>
{smijeVidjetiObvezeTvrtke ? (
          <Link className="font-semibold underline underline-offset-4" to="/obveze-tvrtke">
            Obveze tvrtke →
          </Link>
        ) : null}
</>} />

      <div className="sticky top-0 z-30 -mx-4 border-b border-[var(--color-border)] bg-[var(--color-background)] px-4 md:-mx-8 md:px-8">
        <div role="tablist" aria-label="Pregled rokova i podobnosti" className="flex min-h-14 flex-wrap gap-1">
          {smijePregledati && <>
          <GlavniTab
            id="tab-podobnost"
            aktivan={aktivniTab === "podobnost"}
            onClick={() => setAktivniTab("podobnost")}
          >
            Podobnost zaposlenika
          </GlavniTab>
          <GlavniTab
            id="tab-rokovi"
            aktivan={aktivniTab === "rokovi"}
            onClick={() => setAktivniTab("rokovi")}
          >
            Nadolazeći rokovi
          </GlavniTab>
          </>}
          {smijeVidjetiObvezeTvrtke && <GlavniTab id="tab-company" aktivan={aktivniTab === "company"} onClick={() => setAktivniTab("company")}>Obveze tvrtke</GlavniTab>}
          {smijeVidjetiZadatke && <GlavniTab id="tab-tasks" aktivan={aktivniTab === "tasks"} onClick={() => setAktivniTab("tasks")}>Zadaci</GlavniTab>}
        </div>
      </div>

      {aktivniTab === "company" ? <section role="tabpanel" aria-labelledby="tab-company"><CompanyDeadlines /></section> : aktivniTab === "tasks" ? <section role="tabpanel" aria-labelledby="tab-tasks"><ObligationTasks /></section> : aktivniTab === "podobnost" && podaci ? (
        <section role="tabpanel" aria-labelledby="tab-podobnost">
          {podaci.podobnosti.length === 0 ? (
            <EmptyState
              naslov="Nema aktivnih zaposlenika"
              objasnjenje="Podobnost će se prikazati kada kadrovska unese zaposlenike."
            />
          ) : (
            <div className="avm-surface rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
              <div className="overflow-x-auto md:overflow-visible">
                <table className="w-full min-w-[42rem] border-collapse text-left">
                  <thead className="sticky top-14 z-20 bg-[var(--color-card)]">
                    <tr className="border-b border-[var(--color-border)]">
                      <Zaglavlje>Ime i prezime</Zaglavlje>
                      <Zaglavlje>Radno mjesto</Zaglavlje>
                      <Zaglavlje>Status</Zaglavlje>
                      <Zaglavlje>Razlozi</Zaglavlje>
                    </tr>
                  </thead>
                  <tbody>
                    {podaci.podobnosti.map(({ radnik, pojedinosti }) => {
                      const otvoren = otvoreni === radnik.worker_id;
                      return (
                        <FragmentRedaka
                          key={radnik.worker_id}
                          radnik={radnik}
                          otvoren={otvoren}
                          pojedinosti={pojedinosti}
                          onToggle={() => setOtvoreni(otvoren ? null : radnik.worker_id)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      ) : podaci ? (
        <section role="tabpanel" aria-labelledby="tab-rokovi" className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-[var(--text-card)] font-semibold">Nadolazeći rokovi</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">Odaberite vremensku karticu i tip obveze za uži pregled.</p>
            </div>
            <label className="flex flex-col gap-2">
              <span className="oznaka">Razdoblje</span>
              <DesignSelect
                aria-label="Razdoblje rokova"
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                className="h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3"
              >
                {RAZDOBLJA.map((razdoblje) => (
                  <option key={razdoblje} value={razdoblje}>{razdoblje} dana</option>
                ))}
              </DesignSelect>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-label="Vremenske grupe rokova">
            {VREMENSKE_GRUPE.map((grupa) => {
              const aktivna = vremenskiFiltar === grupa.kljuc;
              return (
                <button
                  key={grupa.kljuc}
                  type="button"
                  aria-pressed={aktivna}
                  onClick={() => setVremenskiFiltar(aktivna ? null : grupa.kljuc)}
                  className={cn(
                    "min-h-20 rounded-[var(--radius-card)] border bg-[var(--color-card)] p-3 text-left transition-colors",
                    grupa.kljuc === "isteklo"
                      ? "border-[var(--color-status-bad-fg)]/30 text-[var(--color-status-bad-fg)]"
                      : "border-[var(--color-border)]",
                    aktivna && "ring-2 ring-[var(--color-nav)] ring-offset-2",
                  )}
                >
                  <span className="oznaka block">{grupa.naziv}</span>
                  <span className="broj mt-1 block text-2xl font-bold">{brojPoGrupi[grupa.kljuc]}</span>
                </button>
              );
            })}
          </div>

          <div>
            <p className="oznaka mb-2">Tip obveze</p>
            <div className="flex flex-wrap gap-2" aria-label="Filtri tipa obveze">
              <button
                type="button"
                aria-pressed={tipovi.size === 0}
                onClick={() => setTipovi(new Set())}
                className={stilChipa(tipovi.size === 0)}
              >
                Svi tipovi
              </button>
              {TIPOVI_OBVEZA.map((tip) => {
                const aktivan = tipovi.has(tip.kljuc);
                return (
                  <button
                    key={tip.kljuc}
                    type="button"
                    aria-pressed={aktivan}
                    onClick={() =>
                      setTipovi((trenutni) => {
                        const novi = new Set(trenutni);
                        if (novi.has(tip.kljuc)) novi.delete(tip.kljuc);
                        else novi.add(tip.kljuc);
                        return novi;
                      })
                    }
                    className={stilChipa(aktivan)}
                  >
                    {tip.naziv}
                  </button>
                );
              })}
            </div>
          </div>

          {podaci.rokovi.length === 0 ? (
            <EmptyState
              naslov="Nema rokova u odabranom razdoblju"
              objasnjenje="Odaberite dulje razdoblje za pregled kasnijih rokova."
            />
          ) : grupiraniRokovi.length === 0 ? (
            <EmptyState
              naslov="Nema rokova za odabrane filtre"
              objasnjenje="Isključite vremenski filtar ili odaberite drugi tip obveze."
            />
          ) : (
            <div className="flex flex-col gap-6">
              {grupiraniRokovi.map((grupa) => (
                <section key={grupa.kljuc} aria-labelledby={`grupa-${grupa.kljuc}`}>
                  <div className={cn(
                    "rounded-t-[var(--radius-card)] border border-b-0 border-[var(--color-border)] bg-[var(--color-muted)] px-4 py-3",
                    grupa.kljuc === "isteklo" && "bg-[var(--color-status-bad-bg)] text-[var(--color-status-bad-fg)]",
                  )}>
                    <h3 id={`grupa-${grupa.kljuc}`} className="font-semibold">{grupa.naziv}</h3>
                    <p className="text-sm">{grupa.rokovi.length} {grupa.rokovi.length === 1 ? "stavka" : "stavki"}</p>
                  </div>
                  <div className="avm-surface rounded-b-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
                    <div className="overflow-x-auto md:overflow-visible">
                      <table className="w-full min-w-[48rem] border-collapse text-left">
                        <thead className="sticky top-14 z-20 bg-[var(--color-card)]">
                          <tr className="border-b border-[var(--color-border)]">
                            <Zaglavlje>Radnik</Zaglavlje>
                            <Zaglavlje>Obveza</Zaglavlje>
                            <Zaglavlje>Datum</Zaglavlje>
                            <Zaglavlje>Dana do isteka</Zaglavlje>
                            <Zaglavlje>Blokira</Zaglavlje>
                          </tr>
                        </thead>
                        <tbody>
                          {grupa.rokovi.map((rok, index) => (
                            <RedakRoka key={`${rok.worker_id}-${rok.item_type}-${rok.due_on}-${index}`} rok={rok} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

function GlavniTab({
  id,
  aktivan,
  children,
  onClick,
}: {
  id: string;
  aktivan: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      id={id}
      type="button"
      role="tab"
      aria-selected={aktivan}
      onClick={onClick}
      className={cn(
        "border-b-2 px-4 text-sm font-medium sm:text-base",
        aktivan
          ? "border-[var(--color-nav)] text-[var(--color-foreground)]"
          : "border-transparent text-[var(--color-muted-foreground)]",
      )}
    >
      {children}
    </button>
  );
}

function RedakRoka({ rok }: { rok: Rok }) {
  const odrediste = odredisteRadnika(rok.worker_id, rok.source, rok.item_type, rok.obligation);
  return (
    <tr className="h-[var(--spacing-row)] border-b border-[var(--color-border)] last:border-b-0">
      <Celija>
        <Link className="font-medium underline underline-offset-2" to={`/zaposlenici/${rok.worker_id}`}>{rok.worker_name}</Link>
      </Celija>
      <Celija><Link className="underline underline-offset-2" to={odrediste}>{rok.obligation}</Link></Celija>
      <Celija><span className="broj">{datum(rok.due_on)}</span></Celija>
      <Celija><span className="broj">{danaDoIsteka(rok.days_until_due)}</span></Celija>
      <Celija><Badge ton={rok.blocking ? "bad" : "idle"}>{rok.blocking ? "Blokira" : "Ne blokira"}</Badge></Celija>
    </tr>
  );
}

function FragmentRedaka({
  radnik,
  otvoren,
  pojedinosti,
  onToggle,
}: {
  radnik: SkupnaPodobnost;
  otvoren: boolean;
  pojedinosti: PojedinostiPodobnosti;
  onToggle: () => void;
}) {
  const kontrola = `razlozi-${radnik.worker_id}`;
  const podobnost = pojedinosti.eligibility;
  const oznaka = podobnost ? oznakaPodobnosti(podobnost.status) : null;
  const brojRazloga = podobnost?.reasons.length ?? 0;

  return (
    <>
      <tr className="h-[var(--spacing-row)] border-b border-[var(--color-border)]">
        <Celija><Link className="font-medium underline underline-offset-2" to={`/zaposlenici/${radnik.worker_id}`}>{radnik.worker_name}</Link></Celija>
        <Celija>{radnik.job_position ?? "—"}</Celija>
        <Celija>{oznaka ? <Badge ton={oznaka.ton}>{oznaka.tekst}</Badge> : <span>Neaktivan</span>}</Celija>
        <Celija>
          <Button variant="outline" aria-expanded={otvoren} aria-controls={kontrola} onClick={onToggle}>
            {otvoren ? "Sakrij razloge" : "Prikaži razloge"} ({brojRazloga})
          </Button>
        </Celija>
      </tr>
      {otvoren ? (
        <tr id={kontrola} className="border-b border-[var(--color-border)] bg-[var(--color-muted)]">
          <td colSpan={4} className="px-4 py-4">
            {!podobnost ? (
              <p className="text-[var(--color-muted-foreground)]">Radnik nije aktivan. Operativna upozorenja se ne računaju.</p>
            ) : podobnost.reasons.length === 0 ? (
              <p className="text-[var(--color-muted-foreground)]">Nema razloga nepodobnosti.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {podobnost.reasons.map((razlog) => (
                  <RazlogRedak key={`${razlog.source}-${razlog.obligation}`} radnikId={radnik.worker_id} razlog={razlog} />
                ))}
              </ul>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RazlogRedak({ radnikId, razlog }: { radnikId: string; razlog: RazlogPodobnosti }) {
  return (
    <li className="flex flex-wrap items-center gap-2">
      <Link
        className="font-medium underline underline-offset-2"
        to={odredisteRadnika(radnikId, razlog.source, "", razlog.obligation)}
      >
        {razlog.obligation}
      </Link>
      <Badge ton="bad">{nazivStanja(razlog.state)}</Badge>
      {razlog.due_on ? <span className="broj text-[var(--color-muted-foreground)]">Rok: {datum(razlog.due_on)}</span> : null}
    </li>
  );
}

function stilChipa(aktivan: boolean): string {
  return cn(
    "min-h-11 rounded-full border px-4 text-sm font-medium",
    aktivan
      ? "border-[var(--color-nav)] bg-[var(--color-nav)] text-white"
      : "border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)]",
  );
}

function Zaglavlje({ children }: { children: React.ReactNode }) {
  return <th className="oznaka bg-[var(--color-card)] px-4 py-3 font-semibold">{children}</th>;
}

function Celija({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3">{children}</td>;
}
